'use client';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import Script from 'next/script';

import {
  ComponentType,
  createElement,
  ReactNode,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react';

import classNames from 'classnames';

import { AiAgent } from '@shared/common/components/AiAgent';
import { useHeaderBannerQuery } from '@shared/hooks/query/headerBanner';
import { useFeatureToggles } from '@shared/hooks/useFeatureToggles';

import { Moon } from '@ui/uikit/components/icons/mono/Moon';
import { Sun } from '@ui/uikit/components/icons/mono/Sun';
import { cn } from '@ui/uikit/utils';
import { createSafeContext, useSafeContext } from '@utilities/context';

import { ADS_CONSTANTS, useUserAdSettings } from './AdBlocks';
import { BuySqrDesktopButton } from './BuySqrDesktopButton';
import { buySqrNavItems, useFeatureNavItems } from './constants';
import { FeaturedNavDesktop } from './FeaturedNavDesktop';
import FeaturedNavMobileBottomBar from './FeaturedNavMobileBottomBar';
import { HeaderBanner } from './HeaderBanner';
import NotificationsButton from './NotificationsButton';
import { PrimaryNavDesktop } from './PrimaryNavDesktop';
import { PrimaryNavMobileMenu } from './PrimaryNavMobileMenu';
import ProductsMenuButton from './ProductsMenuButton';
import { ProfileButton } from './ProfileButton';
import QuickNav from './QuickNav';
import HeaderSearchBar from './Search/HeaderSearchBar';
import MobileSearch from './Search/MobileSearch';
import { NavLayoutLinkBase, NavLayoutUser, PrimaryNavItem } from './types';

const GlobalFloatingBottomRightBannerTextButton = dynamic(
  () =>
    import(
      '@shared/common/components/NavLayout/AdBlocks/GlobalFloatingBottomRightBannerTextButton'
    ),
  {
    ssr: false,
  },
);

const GlobalStickyBottomBannerMobile = dynamic(
  () => import('@shared/common/components/NavLayout/AdBlocks/GlobalStickyBottomBannerMobile'),
  {
    ssr: false,
  },
);

const GlobalFloatingSideBanner = dynamic(
  () => import('@shared/common/components/NavLayout/AdBlocks/GlobalFloatingSideBanner'),
  {
    ssr: false,
  },
);

export interface NavLayoutProps {
  PrimaryNavLink?: ComponentType<{ href?: string; className?: string; children?: ReactNode }>;
  primaryNavItems?: PrimaryNavItem[];
  showMobileFeatureNav?: boolean;
  showKyc?: boolean;
  quickNavItems?: NavLayoutLinkBase[];
  primaryNavMenuTitle?: string;
  showThemeToggle?: boolean;
  children?: ReactNode;
  theme?: string;
  className?: string;
  isLoading?: boolean;
  user?: NavLayoutUser;
  logo?: ReactNode | ComponentType<{ className?: string }>;
  onThemeChange?: (theme: string) => void;
  onLogin?: () => void;
  onLogout?: () => void;
  // TODO: remove when logic will be in uikit
  onBanxaBuyToken?: () => void;
  onSupport?: () => void;
}

interface ContextValue {
  user?: NavLayoutUser;
  onLogin?: () => void;
  onBanxaBuyToken?: () => void;
  showKyc?: boolean;
}

const NavLayoutContext = createSafeContext<ContextValue>();

export const useNavLayout = () => useSafeContext(NavLayoutContext);

export const NavLayout = ({
  onThemeChange,
  theme,
  showMobileFeatureNav,
  children,
  primaryNavItems,
  className,
  isLoading,
  logo,
  user,
  showKyc,
  primaryNavMenuTitle,
  quickNavItems,
  onLogin,
  onLogout,
  onBanxaBuyToken,
  onSupport,
}: NavLayoutProps) => {
  const isConnected = !isLoading && !!user;
  const featuredNavItems = useFeatureNavItems();
  const { showThemeToggle, ...featureToggles } = useFeatureToggles();
  const { data: headerBannerData } = useHeaderBannerQuery();
  const {
    showGlobalFloatingBottomRightBannerTextButton,
    showGlobalStickyHeaderBannerTextButton,
    showGlobalStickyBottomBannerMobile,
    showGlobalFloatingRightBanner,
    showGlobalFloatingLeftBanner,
  } = useUserAdSettings();
  const [hasBottomOffset, setHasBottomOffset] = useState(false);

  const headerBanner = headerBannerData?.isActive ? headerBannerData : undefined;

  const value = useMemo(() => {
    return {
      user,
      onLogin,
      onBanxaBuyToken,
      showKyc,
    };
  }, [user, onLogin, onBanxaBuyToken, showKyc]);

  const hasQuickNav = !!quickNavItems?.length;
  const isHaveHeaderBanner = headerBanner || showGlobalStickyHeaderBannerTextButton;

  const topNotificationHeight = isHaveHeaderBanner
    ? 'var(--nav-layout-header-banner-height)'
    : '0px';
  const headerHeight = hasQuickNav
    ? 'var(--nav-layout-header-height-with-quick-nav)'
    : 'var(--nav-layout-header-height-base)';
  const headerHeightWithTopNotification = `calc(${headerHeight} + ${topNotificationHeight})`;

  useEffect(() => {
    window.document.documentElement.style.setProperty(
      '--nav-layout-header-height',
      headerHeightWithTopNotification,
    );
  }, [headerHeight]);

  // To adjust the FeaturedNavMobileBottomBar offset by 48px as the ad renders with a delay
  useEffect(() => {
    if (showGlobalStickyBottomBannerMobile) {
      setHasBottomOffset(true);
    }
  }, [showGlobalStickyBottomBannerMobile]);

  return (
    <NavLayoutContext.Provider value={value}>
      <div
        style={{
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          '--nav-layout-header-height': headerHeightWithTopNotification,
        }}
        className={classNames(
          'relative flex flex-col flex-1',
          {
            'max-xl-msq:pb-nav-layout-tab-menu': showMobileFeatureNav,
          },
          'pt-[var(--nav-layout-header-height)]',
          // to smoothly shift content to show banner
          'transition-[padding-top]',
          className,
        )}
      >
        <header
          className={classNames(
            'pr-[var(--removed-body-scroll-bar-size)] flex flex-col z-10 border-b border-b-base-border-light fixed left-0 top-0 w-full bg-header-bg backdrop-blur-[64px]',
            'h-[var(--nav-layout-header-height)]',
            // to smoothly increase height of the header to show banner
            'transition-[height]',
          )}
        >
          <HeaderBanner
            headerBanner={headerBanner}
            userId={user?.data.id}
            enabledHeaderAd={showGlobalStickyHeaderBannerTextButton}
            className={classNames(
              'overflow-hidden',
              // to smoothly show banner
              'transition-[height]',
              isHaveHeaderBanner ? 'h-[var(--nav-layout-header-banner-height)]' : 'h-0',
            )}
          />
          <div className={classNames('flex items-center flex-1')}>
            <div className="v2-container flex flex-col gap-2 md-msq:gap-3 justify-center">
              <div className="flex gap-1.5 md-msq:gap-3 items-center">
                {logo && (
                  <Link className="msq-btn" href="/">
                    {typeof logo === 'function'
                      ? createElement(logo, { className: 'size-8' })
                      : logo}
                  </Link>
                )}

                <PrimaryNavMobileMenu
                  onLogin={onLogin}
                  title={primaryNavMenuTitle}
                  isConnected={isConnected}
                  items={primaryNavItems}
                  className="3xl-msq:hidden"
                />

                <MobileSearch />

                {!!primaryNavItems?.length && (
                  <PrimaryNavDesktop
                    onLogin={onLogin}
                    isConnected={isConnected}
                    items={primaryNavItems}
                    className="max-3xl-msq:hidden z-10"
                  />
                )}

                <div className="flex gap-3 ml-auto">
                  <div className="flex">
                    {showThemeToggle && (
                      <button
                        onClick={() => onThemeChange?.(theme === 'dark' ? 'light' : 'dark')}
                        className="msq-btn msq-btn-icon-md msq-btn-ghost size-[2.25rem] text-base-text-primary md-msq:msq-btn-icon-lg"
                      >
                        {theme === 'dark' ? (
                          <Sun className="msq-btn-icon-child-md" />
                        ) : (
                          <Moon className="msq-btn-icon-child-md" />
                        )}
                      </button>
                    )}

                    {isConnected && <NotificationsButton />}
                  </div>

                  <div className="flex gap-3 ml-auto">
                    <div className="flex gap-1.5 max-md-msq:hidden">
                      <BuySqrDesktopButton items={buySqrNavItems} />

                      {/* {isConnected && (
                        <ActivityButtons />
                      )} */}
                    </div>

                    <div className="flex gap-1.5">
                      {!isConnected && (
                        <button
                          onClick={onLogin}
                          className={cn(
                            'msq-btn msq-btn-md !rounded-full min-h-[2.25rem] msq-btn-primary md-msq:msq-btn-lg max-md-msq:text-caption-sm-strong-m',
                            {
                              'msq-btn-loading': isLoading,
                            },
                          )}
                        >
                          Connect
                        </button>
                      )}

                      {isConnected && (
                        <ProfileButton user={user} onSupport={onSupport} onLogout={onLogout} />
                      )}

                      <ProductsMenuButton onLogin={onLogin} />
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={classNames('flex items-center xl-msq:gap-4', {
                  'max-xl-msq:hidden': !hasQuickNav,
                })}
              >
                {hasQuickNav && (
                  <QuickNav
                    items={quickNavItems}
                    isConnected={isConnected}
                    onLogin={onLogin}
                    className="w-full xl-msq:hidden"
                  />
                )}

                <FeaturedNavDesktop
                  user={user}
                  items={featuredNavItems}
                  className="max-xl-msq:hidden"
                />
                <Suspense
                  fallback={<div className="w-full max-xl-msq:hidden skeleton-block h-10"></div>}
                >
                  <HeaderSearchBar className="max-xl-msq:hidden w-full" />
                </Suspense>
              </div>
            </div>
          </div>
        </header>

        {featureToggles?.aiAssistant && <AiAgent />}

        {children}

        {showGlobalFloatingBottomRightBannerTextButton && (
          <GlobalFloatingBottomRightBannerTextButton
            userId={user?.data.id}
            className={classNames({
              'bottom-[72px]': !showGlobalStickyBottomBannerMobile,
              'bottom-[120px]': showGlobalStickyBottomBannerMobile,
            })}
          />
        )}

        {showGlobalFloatingRightBanner && <GlobalFloatingSideBanner userId={user?.data.id} />}

        {showGlobalFloatingLeftBanner && (
          <GlobalFloatingSideBanner userId={user?.data.id} position="left" />
        )}

        {showMobileFeatureNav && (
          <FeaturedNavMobileBottomBar
            items={featuredNavItems}
            user={user}
            // NOTE: if you have to update hidden breakpoint we need to change next things too:
            // * WidgetContainer provider
            // * parent container padding
            className={classNames(
              'fixed left-0 w-full z-10 xl-msq:hidden pr-[var(--removed-body-scroll-bar-size)] transition-[bottom] duration-300',
              {
                'bottom-0': !hasBottomOffset,
                'bottom-[48px]': hasBottomOffset,
              },
            )}
          />
        )}

        {showGlobalStickyBottomBannerMobile && (
          <GlobalStickyBottomBannerMobile userId={user?.data.id} />
        )}
      </div>
      <Script async src={ADS_CONSTANTS.scriptCoinTrafficUrl} />
    </NavLayoutContext.Provider>
  );
};
